/**
 * baseFilterSchema — 관리자 목록 API 공통 필터 스키마
 *
 * 모든 목록 페이지에 공통으로 들어가는 필드를 정의한다.
 * .extend()로 프로젝트별 필드를 추가해서 사용한다.
 *
 * @example
 * // 기본 그대로 사용
 * type MemberFilter = BaseFilter;
 * const init = baseFilterDefaults;
 *
 * @example
 * // 프로젝트별 필드 추가
 * const memberFilterSchema = baseFilterSchema.extend({
 *   role:   z.string().default(""),
 *   grade:  z.string().default(""),
 * });
 * type MemberFilter = z.infer<typeof memberFilterSchema>;
 * const initFilters = memberFilterSchema.parse({});  // 모든 default 적용
 *
 * @example
 * // API Route에서 query params 파싱
 * export async function GET(req: NextRequest) {
 *   const filter = parseSearchParams(req.nextUrl.searchParams);
 *   // filter.keyword, filter.page, filter.pageSize ...
 * }
 */

import { z } from "zod";

export const baseFilterSchema = z.object({
    /** 키워드 검색 타입 (예: "name" | "email") */
    keywordType: z.string().default(""),
    /** 키워드 검색어 */
    keyword: z.string().default(""),
    /** 날짜 필터 타입 (예: "createdAt" | "updatedAt") */
    dateType: z.string().default(""),
    /** 시작일 (YYYY-MM-DD) */
    startDate: z.string().default(""),
    /** 종료일 (YYYY-MM-DD) */
    endDate: z.string().default(""),
    /** 자유 형식 옵션 필터 (프로젝트별로 원하는 키/값을 자유롭게 담아 쓴다) */
    options: z.record(z.string(), z.any()).default({}),
    /**
     * 현재 페이지 (1부터 시작). 안 보내면 undefined — 페이지네이션 없이 전체 조회하는
     * 케이스(옵션 목록 등)와 구분하기 위해 기본값을 강제하지 않는다.
     * 페이지네이션이 필요한 API에서는 호출부에서 data.page ?? 1 로 기본값을 직접 적용한다.
     */
    page: z.coerce.number().int().positive().optional(),
    /** 페이지당 행 수. 안 보내면 undefined (위 page와 동일한 이유) */
    pageSize: z.coerce.number().int().positive().optional(),
});

export type BaseFilter = z.infer<typeof baseFilterSchema>;

/** 초기값 — useState 초기화에 사용 */
export const baseFilterDefaults: BaseFilter = baseFilterSchema.parse({});

/**
 * URLSearchParams → BaseFilter 파싱
 * API Route에서 query params를 안전하게 파싱할 때 사용한다.
 */
export function parseSearchParams(searchParams: URLSearchParams): BaseFilter {
    return baseFilterSchema.parse(Object.fromEntries(searchParams));
}

/**
 * 날짜 범위 유효성 검사 refine
 * baseFilterSchema.extend({...}).superRefine(dateRangeRefine) 형태로 사용한다.
 *
 * @example
 * const memberFilterSchema = baseFilterSchema
 *   .extend({ role: z.string().default("") })
 *   .superRefine(dateRangeRefine);
 */
export function dateRangeRefine(
    data: { startDate?: string; endDate?: string },
    ctx: z.RefinementCtx
) {
    if (data.startDate && data.endDate && data.startDate > data.endDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "시작일이 종료일보다 클 수 없습니다.",
            path: ["endDate"],
        });
    }
}
